import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ShoppingCart, User, Store, MapPin, Check, Plus, Minus, X } from 'lucide-react';
import { formatCurrency } from '@/lib/utils-helpers';

const STEPS = [
  { num: 1, label: 'Customer', icon: User },
  { num: 2, label: 'Vendor & Menu', icon: Store },
  { num: 3, label: 'Delivery Details', icon: MapPin },
  { num: 4, label: 'Review & Submit', icon: Check },
];

export default function CreateOrder() {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // Step 1: Customer data
  const [customerMode, setCustomerMode] = useState('existing'); // existing or new
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState({ email: '', name: '', phone: '' });

  // Step 2: Vendor & Menu data
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [cart, setCart] = useState([]);

  // Step 3: Delivery data
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('paid');

  // Fetch data on mount
  useEffect(() => {
    fetchCustomers();
    fetchVendors();
    fetchDrivers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const res = await api.get('/admin/users?role=customer&limit=100');
      setCustomers(res.data.users || []);
    } catch (err) {
      console.error('Failed to load customers');
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await api.get('/admin/vendors?limit=100');
      setVendors(res.data.vendors || []);
    } catch (err) {
      console.error('Failed to load vendors');
    }
  };

  const fetchDrivers = async () => {
    try {
      const res = await api.get('/admin/drivers?limit=100');
      setDrivers(res.data.drivers || []);
    } catch (err) {
      console.error('Failed to load drivers');
    }
  };

  const fetchMenuItems = async (vendorId) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/vendors/${vendorId}/menu`);
      setMenuItems(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load menu items');
    } finally {
      setLoading(false);
    }
  };

  const handleVendorSelect = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    setSelectedVendor(vendor);
    setCart([]);
    fetchMenuItems(vendorId);
  };

  const addToCart = (item) => {
    const existing = cart.find(c => c.menu_item_id === item.id);
    if (existing) {
      setCart(cart.map(c => 
        c.menu_item_id === item.id 
          ? { ...c, quantity: c.quantity + 1 }
          : c
      ));
    } else {
      setCart([...cart, {
        menu_item_id: item.id,
        name: item.name,
        price: item.price,
        quantity: 1
      }]);
    }
  };

  const updateQuantity = (itemId, delta) => {
    setCart(cart.map(c => {
      if (c.menu_item_id === itemId) {
        const newQty = c.quantity + delta;
        return newQty > 0 ? { ...c, quantity: newQty } : null;
      }
      return c;
    }).filter(Boolean));
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(c => c.menu_item_id !== itemId));
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const deliveryFee = 3.99;
    const serviceFee = subtotal * 0.10;
    const total = subtotal + deliveryFee + serviceFee;
    return { subtotal, deliveryFee, serviceFee, total };
  };

  const validateStep = () => {
    if (currentStep === 1) {
      if (customerMode === 'existing' && !selectedCustomer) {
        toast.error('Please select a customer');
        return false;
      }
      if (customerMode === 'new' && (!newCustomer.email || !newCustomer.name)) {
        toast.error('Email and name are required for new customer');
        return false;
      }
    }
    if (currentStep === 2) {
      if (!selectedVendor) {
        toast.error('Please select a vendor');
        return false;
      }
      if (cart.length === 0) {
        toast.error('Please add at least one item to cart');
        return false;
      }
    }
    if (currentStep === 3) {
      if (!deliveryAddress.trim()) {
        toast.error('Delivery address is required');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setCurrentStep(currentStep + 1);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setSubmitting(true);
    try {
      const customerId = customerMode === 'existing' 
        ? selectedCustomer.id 
        : 'new';

      const params = new URLSearchParams({
        customer_id: customerId,
        vendor_id: selectedVendor.id,
        items: JSON.stringify(cart),
        delivery_address: deliveryAddress,
        payment_status: paymentStatus
      });

      if (customerMode === 'new') {
        params.append('customer_email', newCustomer.email);
        params.append('customer_name', newCustomer.name);
        params.append('customer_phone', newCustomer.phone || '');
      }

      if (selectedDriver) {
        params.append('driver_id', selectedDriver.id);
      }

      await api.post(`/admin/orders/create?${params}`);
      toast.success('Order created successfully!');
      navigate('/admin/orders');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Customer Selection
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Customer Type</Label>
              <Select value={customerMode} onValueChange={setCustomerMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="existing">Existing Customer</SelectItem>
                  <SelectItem value="new">New Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {customerMode === 'existing' ? (
              <div className="space-y-2">
                <Label>Select Customer *</Label>
                <Select value={selectedCustomer?.id} onValueChange={id => setSelectedCustomer(customers.find(c => c.id === id))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a customer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name} ({customer.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedCustomer && (
                  <div className="p-3 bg-muted/50 rounded text-sm">
                    <p><strong>Name:</strong> {selectedCustomer.name}</p>
                    <p><strong>Email:</strong> {selectedCustomer.email}</p>
                    <p><strong>Phone:</strong> {selectedCustomer.phone || 'N/A'}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newCustomer.email}
                    onChange={e => setNewCustomer({...newCustomer, email: e.target.value})}
                    placeholder="customer@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={newCustomer.name}
                    onChange={e => setNewCustomer({...newCustomer, name: e.target.value})}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    value={newCustomer.phone}
                    onChange={e => setNewCustomer({...newCustomer, phone: e.target.value})}
                    placeholder="+1-555-0100"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 2: // Vendor & Menu Selection
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Vendor *</Label>
              <Select value={selectedVendor?.id} onValueChange={handleVendorSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a vendor..." />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map(vendor => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name} - {vendor.category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedVendor && (
              <>
                <Separator />
                <div className="grid grid-cols-2 gap-4">
                  {/* Menu Items */}
                  <div className="space-y-2">
                    <h3 className="font-medium">Menu Items</h3>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {loading ? (
                        <p className="text-sm text-muted-foreground">Loading menu...</p>
                      ) : menuItems.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No menu items found</p>
                      ) : (
                        menuItems.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-2 border rounded hover:bg-muted/50">
                            <div>
                              <p className="font-medium text-sm">{item.name}</p>
                              <p className="text-xs text-muted-foreground">{formatCurrency(item.price)}</p>
                            </div>
                            <Button size="sm" onClick={() => addToCart(item)}>
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Cart */}
                  <div className="space-y-2">
                    <h3 className="font-medium">Cart ({cart.length} items)</h3>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {cart.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Cart is empty</p>
                      ) : (
                        cart.map(item => (
                          <div key={item.menu_item_id} className="flex items-center justify-between p-2 border rounded">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{item.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatCurrency(item.price)} × {item.quantity} = {formatCurrency(item.price * item.quantity)}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button size="sm" variant="outline" onClick={() => updateQuantity(item.menu_item_id, -1)}>
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="text-sm w-8 text-center">{item.quantity}</span>
                              <Button size="sm" variant="outline" onClick={() => updateQuantity(item.menu_item_id, 1)}>
                                <Plus className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => removeFromCart(item.menu_item_id)}>
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );

      case 3: // Delivery Details
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Delivery Address *</Label>
              <Textarea
                value={deliveryAddress}
                onChange={e => setDeliveryAddress(e.target.value)}
                placeholder="123 Main Street, Apt 4B, New York, NY 10001"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Assign Driver (Optional)</Label>
              <Select value={selectedDriver?.id || 'none'} onValueChange={id => setSelectedDriver(id === 'none' ? null : drivers.find(d => d.id === id))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Auto-dispatch (no driver assigned)</SelectItem>
                  {drivers.filter(d => d.is_online && d.ready_to_accept).map(driver => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.name} - {driver.vehicle_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {selectedDriver ? 'Order will be assigned to selected driver' : 'Order will be auto-dispatched to available drivers'}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Payment Status</Label>
              <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">Paid (Admin-processed)</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 4: // Review & Submit
        const { subtotal, deliveryFee, serviceFee, total } = calculateTotals();
        return (
          <div className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg space-y-3">
              <div>
                <h3 className="font-medium mb-2">Customer</h3>
                <p className="text-sm">
                  {customerMode === 'existing' 
                    ? `${selectedCustomer?.name} (${selectedCustomer?.email})`
                    : `${newCustomer.name} (${newCustomer.email}) - New Customer`
                  }
                </p>
              </div>

              <Separator />

              <div>
                <h3 className="font-medium mb-2">Vendor</h3>
                <p className="text-sm">{selectedVendor?.name}</p>
              </div>

              <Separator />

              <div>
                <h3 className="font-medium mb-2">Items ({cart.length})</h3>
                <div className="space-y-1">
                  {cart.map(item => (
                    <div key={item.menu_item_id} className="flex justify-between text-sm">
                      <span>{item.name} × {item.quantity}</span>
                      <span>{formatCurrency(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="font-medium mb-2">Delivery</h3>
                <p className="text-sm">{deliveryAddress}</p>
                {selectedDriver && (
                  <p className="text-sm text-muted-foreground mt-1">Driver: {selectedDriver.name}</p>
                )}
              </div>

              <Separator />

              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Delivery Fee</span>
                  <span>{formatCurrency(deliveryFee)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Service Fee (10%)</span>
                  <span>{formatCurrency(serviceFee)}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 p-2 bg-background rounded">
                <Badge variant={paymentStatus === 'paid' ? 'default' : 'secondary'}>
                  {paymentStatus === 'paid' ? 'Paid' : 'Pending Payment'}
                </Badge>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Create New Order</h1>
        <p className="text-muted-foreground mt-1">Create an order on behalf of a customer</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between">
        {STEPS.map((step, idx) => {
          const Icon = step.icon;
          const isActive = step.num === currentStep;
          const isComplete = step.num < currentStep;
          return (
            <React.Fragment key={step.num}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                  isComplete ? 'bg-emerald-100 text-emerald-700' :
                  isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  {isComplete ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                </div>
                <p className={`text-xs font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {step.label}
                </p>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 mb-6 ${
                  step.num < currentStep ? 'bg-emerald-500' : 'bg-muted'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content */}
      <Card>
        <CardHeader>
          <CardTitle>Step {currentStep}: {STEPS[currentStep - 1].label}</CardTitle>
        </CardHeader>
        <CardContent>
          {renderStepContent()}

          {/* Navigation */}
          <div className="flex justify-between mt-6 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : navigate('/admin/orders')}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              {currentStep > 1 ? 'Back' : 'Cancel'}
            </Button>

            {currentStep < 4 ? (
              <Button onClick={handleNext}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Order'}
                <ShoppingCart className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
